import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { baselineKeys } from "@/hooks/use-baseline";
import { solutionKeys } from "@/lib/query";

export interface PreviewResult {
  counts: {
    deliverables: number;
    epics: number;
    stories: number;
    acceptanceCriteria: number;
  };
  errors: { row: number; column?: string; message: string }[];
}

interface CommitCounts {
  counts: PreviewResult["counts"];
}

/**
 * `errorCode` carries the server's typed error code so callers can branch
 * (e.g. BASELINE_INVALID surfaces row-level errors; BASELINE_EXISTS prompts
 * a "use Replace instead" message).
 */
export class BaselineImportError extends Error {
  constructor(
    public errorCode: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "BaselineImportError";
  }
}

async function postBaseline<T>(url: string, csvText: string): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ csvText }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    const err = json?.error;
    throw new BaselineImportError(
      err?.code ?? "UNKNOWN",
      err?.message ?? "Request failed",
      err?.details,
    );
  }
  return json.data as T;
}

export function usePreviewBaseline(solutionId: string) {
  return useMutation({
    mutationFn: (csvText: string) =>
      postBaseline<PreviewResult>(`/api/solutions/${solutionId}/baseline/preview`, csvText),
  });
}

export function useCommitBaseline(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (csvText: string) =>
      postBaseline<CommitCounts>(`/api/solutions/${solutionId}/baseline/commit`, csvText),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baselineKeys.tree(solutionId) });
      qc.invalidateQueries({ queryKey: solutionKeys.detail(solutionId) });
      toast.success("Baseline imported");
    },
    onError: (err: BaselineImportError) => {
      // Validation failures are surfaced inline in the preview UI — don't toast.
      if (err.errorCode === "BASELINE_INVALID") return;
      toast.error(err.message);
    },
  });
}

export function useReplaceBaseline(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (csvText: string) =>
      postBaseline<CommitCounts>(`/api/solutions/${solutionId}/baseline/replace`, csvText),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baselineKeys.tree(solutionId) });
      qc.invalidateQueries({ queryKey: solutionKeys.detail(solutionId) });
      toast.success("Baseline replaced");
    },
    onError: (err: BaselineImportError) => {
      if (err.errorCode === "BASELINE_INVALID") return;
      toast.error(err.message);
    },
  });
}
